/**
 * DashboardProjectsTab.jsx — Phase 147 (Dashboard Cleanup), extracted from
 * Dashboard.jsx unchanged (Projects tab body: tiles, project alerts,
 * "Needs you" inbox, overdue milestones, recent projects, BU leaderboard,
 * report view, goals).
 */
import React from "react";
import { Link } from "react-router-dom";
import { Tag } from "@/components/Layout";
import BuLeaderboardCard from "@/components/BuLeaderboardCard";
import ProcurementSpendWidgets from "@/components/ProcurementSpendWidgets";
import { Card, TileLink } from "@/components/dashboard/DashboardCards";
import TabSection from "@/components/dashboard/TabSection";
import { approvalTone, NEEDS_YOU_KIND_LABELS, goalTone } from "@/lib/dashboardHelpers";
import { formatDate } from "@/lib/dateFormat";
import {
  FolderKanban, ListChecks, CheckCircle2, Target, AlertTriangle, Inbox,
  ChevronRight, CalendarDays, Award,
} from "lucide-react";

export default function DashboardProjectsTab({
  activeTab, activeProjects, openMilestones, openReviewsCount, goals,
  projectAlerts, needsYouInbox, needsYouKind, setNeedsYouKind, needsYouByKind,
  approvals, overdueOpenTasks, upcomingMilestones, projects, myProjects,
  milestonesByProject, canWeeklyReports, canPersonalGoals, todaysGoals, topAchievements,
}) {
  return (
    <TabSection activeTab={activeTab} id="projects">
      {/* Phase 120 — relocated from Overview: Active Projects / Open
          Milestones / Open Approvals / My Goals now live here so the
          Overview landing page stays task-first. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <TileLink to="/my-work?tab=projects&status=active"     label="Active projects"     value={activeProjects}    icon={FolderKanban} testId="tile-active-projects" />
        <TileLink to="/my-work?tab=projects&filter=open-milestones" label="Open milestones" value={openMilestones} icon={ListChecks}   accent={openMilestones > 0 ? "text-[#F54800]" : ""} testId="tile-open-milestones" />
        <TileLink to="/approvals?type=review"        label="Open approvals"        value={openReviewsCount} icon={CheckCircle2} accent={openReviewsCount > 0 ? "text-orange-700" : ""} testId="tile-open-reviews" />
        <TileLink to="/my-work?tab=goals"                      label="My goals"            value={goals.length}      icon={Target}       testId="tile-my-goals" />
      </div>

      {/* Phase 84.63 — Red alert: past deadline + open Launch Gates.
          Phase 116 — moved here from Overview so it only shows up when
          the Projects tab is actually selected. */}
      {projectAlerts.length > 0 && (
        <div className="border border-red-200 bg-red-50 rounded-sm p-4 mb-6 flex items-start gap-3" data-testid="dashboard-project-alerts">
          <AlertTriangle size={18} className="text-red-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-display font-bold text-sm text-red-800">
              {projectAlerts.length} project{projectAlerts.length !== 1 ? "s" : ""} past deadline with open Launch Gates
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {projectAlerts.map((p) => (
                <Link
                  key={p.project_id}
                  to={`/projects/${p.project_id}`}
                  className="text-xs font-medium bg-white border border-red-300 text-red-700 rounded-sm px-2 py-1 hover:bg-red-100"
                  data-testid={`dashboard-alert-project-${p.project_id}`}
                >
                  {p.name} · {p.launch_gates_open} gate{p.launch_gates_open !== 1 ? "s" : ""} open
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* "Needs you" — Phase 67 (iter51): live unified-approvals feed +
          per-kind chip filters. Phase 116 — moved here from Overview to
          keep the initial landing page task-first / uncluttered. */}
      <div className="card-flat overflow-hidden mb-6" data-testid="card-needs-you">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Inbox size={14} className="text-[#F54800] shrink-0" />
            <div>
              <div className="font-display font-bold text-sm text-slate-900">Needs you</div>
              <div className="text-[10px] text-slate-500">{needsYouInbox.length + overdueOpenTasks.length} item{needsYouInbox.length + overdueOpenTasks.length === 1 ? "" : "s"} · most overdue first</div>
            </div>
          </div>
          <Link to="/approvals" className="text-[11px] text-[#F54800] hover:underline inline-flex items-center gap-0.5 shrink-0">View all <ChevronRight size={12} /></Link>
        </div>

        {/* Phase 67 (iter51) — Per-kind filter strip. Mirrors the chip-grid on
            the Approvals page so users develop muscle memory between the two
            surfaces. "All" pinned first; chips only appear for kinds that
            actually have at least one open item, keeping the strip lean. */}
        {needsYouInbox.length > 0 && (
          <div className="px-5 pt-2 pb-1 flex items-center gap-1 overflow-x-auto border-b border-slate-100" data-testid="needs-you-kind-tabs">
            <button
              type="button"
              onClick={() => setNeedsYouKind("all")}
              className={`shrink-0 px-2 py-0.5 text-[10px] rounded-sm border whitespace-nowrap transition-colors ${
                needsYouKind === "all"
                  ? "bg-slate-900 border-slate-900 text-white"
                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
              data-testid="needs-you-kind-all"
            >
              All <span className={`text-[9px] font-mono px-1 rounded-sm ml-0.5 ${needsYouKind === "all" ? "bg-white/20" : "bg-slate-100"}`}>{needsYouInbox.length}</span>
            </button>
            {Object.entries(NEEDS_YOU_KIND_LABELS)
              .filter(([k]) => (needsYouByKind[k] || 0) > 0)
              .map(([k, lbl]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setNeedsYouKind(k)}
                  className={`shrink-0 px-2 py-0.5 text-[10px] rounded-sm border whitespace-nowrap transition-colors ${
                    needsYouKind === k
                      ? "bg-slate-900 border-slate-900 text-white"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`needs-you-kind-${k}`}
                >
                  {lbl} <span className={`text-[9px] font-mono px-1 rounded-sm ml-0.5 ${needsYouKind === k ? "bg-white/20" : "bg-slate-100"}`}>{needsYouByKind[k]}</span>
                </button>
              ))}
          </div>
        )}

        <div className="p-4 min-h-[120px]">
          {approvals.length === 0 && overdueOpenTasks.length === 0 ? (
            <div className="text-sm text-slate-500" data-testid="needs-you-empty">
              {needsYouKind === "all"
                ? "Inbox zero. Everything is moving."
                : `No ${(NEEDS_YOU_KIND_LABELS[needsYouKind] || needsYouKind).toLowerCase()} requests waiting. Switch back to All.`}
            </div>
          ) : (
            <ul className="space-y-2">
              {approvals.map((a, i) => (
                <li key={`${a.kind}:${i}`} className="flex items-center justify-between gap-3 text-sm" data-testid={`needs-you-approval-${i}`}>
                  <Link to={a.to} className="min-w-0 flex-1 hover:text-[#F54800]">
                    <div className="font-medium text-slate-800 truncate">{a.title}</div>
                    <div className="text-xs text-slate-500 truncate">{a.hint}</div>
                  </Link>
                  <Tag tone={approvalTone(a.kind)}>{NEEDS_YOU_KIND_LABELS[a.kind] || a.kind}</Tag>
                </li>
              ))}
              {/* Report-task overdue rows only relevant on the "All" view since
                  they aren't approvals per se. */}
              {needsYouKind === "all" && overdueOpenTasks.slice(0, 3).map((t) => (
                <li key={`ot:${t.id}`} className="flex items-center justify-between gap-3 text-sm" data-testid={`needs-you-task-${t.id}`}>
                  <Link to="/my-work?tab=reviews" className="min-w-0 flex-1 hover:text-[#F54800]">
                    <div className="font-medium text-slate-800 truncate">{t.title}</div>
                    <div className="text-xs text-slate-500 truncate">Report fix needed</div>
                  </Link>
                  <Tag tone="orange">report</Tag>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Overdue milestones */}
        <Card title="Overdue milestones" hint="Most overdue first · upcoming + active" to="/my-work?tab=projects&filter=open-milestones" icon={ListChecks} testId="card-milestones">
          {upcomingMilestones.length === 0 ? (
            <div className="text-sm text-slate-500">All caught up — no upcoming milestones.</div>
          ) : (
            <ul className="space-y-2">
              {upcomingMilestones.map((m) => {
                const project = projects.find((p) => p.id === m.project_id);
                return (
                  <li key={m.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`milestone-row-${m.id}`}>
                    <Link to={`/projects/${m.project_id}`} className="min-w-0 flex-1 hover:text-[#F54800]">
                      <div className="font-medium text-slate-800 truncate">{m.title}</div>
                      <div className="text-xs text-slate-500 truncate">{project?.name}</div>
                    </Link>
                    <Tag tone={m._overdue ? "red" : (m.status === "in_progress" ? "orange" : "slate")}>{m._overdue ? `overdue ${formatDate(m.due_date)}` : (formatDate(m.due_date, { fallback: "—" }))}</Tag>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {/* Recent active projects */}
        <Card title="Recent active projects" to="/my-work?tab=projects" icon={FolderKanban} testId="card-recent-projects">
          {myProjects.length === 0 ? (
            <div className="text-sm text-slate-500">No active projects assigned to you.</div>
          ) : (
            <ul className="space-y-2">
              {myProjects.map((p) => {
                const ms = milestonesByProject[p.id] || [];
                const done = p.milestones_done ?? ms.filter((m) => m.status === "done").length;
                const total = p.milestones_total ?? ms.length;
                const pct = p.progress_pct ?? (total ? Math.round((done / total) * 100) : 0);
                return (
                  <li key={p.id}>
                    <Link to={`/projects/${p.id}`} className="flex items-center justify-between gap-3 text-sm hover:text-[#F54800]" data-testid={`recent-project-${p.id}`}>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800 truncate">{p.name}</div>
                        <div className="text-xs text-slate-500 truncate">{p.client_name} · {p.business_unit}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono text-xs text-slate-600" data-testid={`recent-pct-${p.id}`}>{pct}%</div>
                        <div className="text-[10px] text-slate-400">{done}/{total} ms</div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {/* Phase 121 — Business Unit Leaderboard: milestone completion rate, BU vs BU. */}
        <BuLeaderboardCard />

        {/* Report view */}
        <Card title="Report view" hint="Most overdue first" to={canWeeklyReports ? "/my-work?tab=weekly" : null} icon={CalendarDays} testId="card-reports">
          <div className="text-sm text-slate-700">
            {overdueOpenTasks.length > 0 ? (
              <>
                <div className="flex items-center gap-1.5 text-orange-700 mb-2"><AlertTriangle size={12} /> {overdueOpenTasks.length} review {overdueOpenTasks.length === 1 ? "task" : "tasks"} need your fix</div>
                <ul className="space-y-1">
                  {overdueOpenTasks.slice(0, 5).map((t) => (
                    <li key={t.id} className="truncate">→ <Link to="/my-work?tab=reviews" className="hover:text-[#F54800]">{t.title}</Link></li>
                  ))}
                </ul>                </>
            ) : (
              <div className="text-slate-500">Your weekly summaries are up to date.</div>
            )}
          </div>
        </Card>

        {/* Goals + achievements */}
        <Card title="Goals" hint="Top achievements below" to={canPersonalGoals ? "/my-work?tab=goals" : null} icon={Target} testId="card-goals">
          {todaysGoals.length === 0 ? (
            <div className="text-sm text-slate-500">No active goals. Set one to track progress.</div>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {todaysGoals.map((g) => (
                <li key={g.id} className="flex items-center justify-between">
                  <span className="text-slate-800 truncate">{g.title}</span>
                  <Tag tone={goalTone(g.status)}>{(g.status || "open").replace("_", " ")}</Tag>
                </li>
              ))}
            </ul>
          )}
          {topAchievements.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="label-overline mb-1.5">Top achievements</div>
              <ul className="space-y-1">
                {topAchievements.map((a) => (
                  <li key={a.id} className="text-xs text-slate-700 inline-flex items-center gap-1.5"><Award size={11} className="text-[#F54800]" /> {a.title}</li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      </div>
      <ProcurementSpendWidgets />
    </TabSection>
  );
}
